import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { installClientErrorCapture } from "@/lib/clientErrorCapture";
import { isFullwidthPath } from "@/lib/fullwidth-routes";
import { installChunkLoadRecovery, renderBootFallback } from "@/lib/startupRecovery";
import { clearLegacyBackendAuthStorage, validateBackendConfig } from "@/lib/backendConfigGuard";
import "./index.css";

try { clearLegacyBackendAuthStorage(); } catch { /* ignore */ }
try { installClientErrorCapture(); } catch { /* ignore */ }
try { installChunkLoadRecovery(); } catch { /* ignore */ }

declare global {
  interface Window {
    __whispraiMarkMounted?: () => void;
  }
}

const BootMountMarker = () => {
  useEffect(() => {
    try {
      window.__whispraiMarkMounted?.();
    } catch { /* ignore */ }
  }, []);
  return null;
};

if (typeof window !== "undefined") {
  const rootEl = document.getElementById("root");
  if (rootEl && isFullwidthPath(window.location.pathname)) {
    rootEl.classList.add("fullwidth");
  }
}

const container = document.getElementById("root");
async function mountApp() {
  if (!container) return;

  try {
    const issue = validateBackendConfig();
    if (issue) {
      // eslint-disable-next-line no-console
      console.error("[boot] backend config issue", issue);
    }
  } catch { /* ignore */ }

  try {
    const { default: App } = await import("./App");
    const existing = (container as any).__reactRoot;
    const root = existing ?? createRoot(container);
    if (!existing) (container as any).__reactRoot = root;
    root.render(
      <HelmetProvider>
        <ErrorBoundary>
          <BootMountMarker />
          <App />
        </ErrorBoundary>
      </HelmetProvider>
    );
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    const stack = err instanceof Error ? err.stack : undefined;
    try { console.error("[boot] React mount failed", err); } catch { /* ignore */ }
    try { renderBootFallback(container, msg, stack); } catch { /* ignore */ }
  }
}

void mountApp();
